import { encounterDefaults, encounterDefaultSaveFields, encounterCreateCatalogRefusal, encounterCreateZone, type EncounterDefaults } from '../janus-engine/authoring/encounter-defaults';
import { markCampaignHouseRuleGroups, saveCampaignHouseRuleToggle } from './house-rules';

// encounter-admin.ts  GM campaign/encounter admin + turn-timer action handlers
// (template.js cleanup roadmap Tier 3 � T3.3, 2026-06-16).
//
// Byte-faithful port of the GM-admin + turn-timer layer carved out of template.js. Encounter/campaign-
// state mutating (campaign running/link, location copy/delete, XP award) + live combat-timer control;
// ported verbatim, only `: any` params + DOM casts + `!` were added for strict TS.
//
// Public surface (all window-attached  PHP onclick + turn-widget bundle onclick strings + the
// my_campaign.php setInterval driver, all runtime-invoked):
//   runTurnTimerTick (my_campaign.php setInterval), awardExperienceMarks (form_award_experience_marks_inc.php),
//   viewEncounterLinks / viewPublicEncounters / viewPrivateEncounters / viewAllEncounters (turn-widget
//   gm-controls onclick + my_campaign.php), copyAdventureToMasterParentID (adventure/locations/my_encounters),
//   copySelectedLocationsToMasterParentID, deleteAllLocationsExceptCampsiteAndStronghold, linkPlayerToCampaign
//   (link_player.php), toggleCampaignRunning (table_list_my_campaigns_inc.php), toggleTurnTimer / resetTurnTimer /
//   addTurnTimerSeconds (turn-widget gm-controls onclick), log_15_second_warning / log_10_second_warning.
//
// NOTE on the turn-timer split: the live-mirror / broadcast / ticker cluster (ftApplyTurnTimerMirror,
// broadcastTurnTimer, ftSyncTurnTimerMirror, ftEnsureTurnTimerMirrorTicker, ftRenderTurnTimerMirrorBadge,
// getDefaultCreatureTurnTimerSecondsSync) is now FULLY REUNITED into THIS bundle (T4.6p2 moved gmControls* +
// T4.7 2026-06-17 moved the whole mirror/broadcast/ticker cluster) so the auto-end path + every mirror call is in-bundle]  these carved handlers
// reach it as window globals at runtime. Shared timer state lives in hidden DOM inputs + window.FateTide.*
// (NOT a closed-over top-level let), so the split is safe. light/weather forms are already in turn-widget.
//
// Loaded AFTER template.js; every dep resolves at call time (onclick / form-button / setInterval / AJAX).


// ===========================================================================================
// runTurnTimerTick  (carved from template.js 5380-5455)
// ===========================================================================================
import { encounterField } from '../shared/array-types.js';
import { planExperienceAward } from '../janus-engine/advancement/experience-marks';
import { withLogParticipants } from '../shared/log-participant-snapshot';

type TurnTimerState = {
    remaining?: number;
    running?: number;
    auto_end_turn?: number;
};
type TurnTimerMirror = { remaining: number; running: number; encounterId: string };
interface EncounterAdminState {
    encounterCache?: Map<string, Record<string, unknown>>;
    turnTimer?: TurnTimerState;
    turnTimerAutoEnding?: boolean;
    turnTimerWarningIssued?: boolean;
    turnTimerMirror?: TurnTimerMirror;
    _turnTimerMirrorTicker?: ReturnType<typeof setInterval>;
    user?: { isGM?: boolean };
    campaign?: { id?: string; encounterId?: string };
}
interface EncounterAdminHost {
    FateTide?: EncounterAdminState;
    ftGetTurnTimerState?: () => TurnTimerState | null;
    ftSetTurnTimerState?: (ctx: TurnTimerState, source: string) => TurnTimerState | null;
    ftGetActiveEncounterId?: () => string;
    ftGetCampaignId?: () => string;
    ftGetEncounterContext?: () => FtSidelogContext | null;
}
function encounterAdminHost(): Window & EncounterAdminHost {
    return window as unknown as Window & EncounterAdminHost;
}
function encounterAdminState(): EncounterAdminState {
    return encounterAdminHost().FateTide ??= {};
}

function getTurnTimerState(): TurnTimerState {
    const getter = encounterAdminHost().ftGetTurnTimerState;
    if (typeof getter === 'function') {
        return getter() || {};
    }
    // Compatibility fallback only. Normal runtime state is window.FateTide.turnTimer via template.js.
    const cached = encounterAdminHost().FateTide?.turnTimer || {};
    const rem = parseInt(String(cached.remaining ?? 0), 10);
    return {
        remaining: Number.isNaN(rem) ? 0 : Math.max(0, rem),
        running: cached.running === 1 ? 1 : 0,
        auto_end_turn: cached.auto_end_turn === 1 ? 1 : 0,
    };
}

function setTurnTimerState(ctx: TurnTimerState, source: string): TurnTimerState {
    const setter = encounterAdminHost().ftSetTurnTimerState;
    if (typeof setter === 'function') {
        return setter(ctx, source) || {};
    }
    const state = { ...getTurnTimerState(), ...ctx };
    const rem = Math.max(0, parseInt(String(state.remaining ?? 0), 10) || 0);
    const running = state.running ? 1 : 0;
    const autoEnd = state.auto_end_turn === 1 ? 1 : 0;
    const spanRem = document.getElementById('span_creature_turn_timer_remaining');
    if (spanRem) spanRem.textContent = String(rem);
    return encounterAdminState().turnTimer = { remaining: rem, running, auto_end_turn: autoEnd };
}

function runTurnTimerTick() {
    // Widget-presence guard. The helper/cache is authoritative once the visible widget exists.
    const spanRemaining = (document.getElementById('span_creature_turn_timer_remaining') as HTMLInputElement);
    if (!spanRemaining) {
        return;
    }

    window.FateTide = window.FateTide || {};

    const timerState = getTurnTimerState();
    if (timerState.running !== 1) {
        return;
    }

    const turnTimerRemaining = Math.max(0, parseInt(String(timerState.remaining ?? 0), 10) || 0);
    const newTurnTimerRemaining = turnTimerRemaining - 1;
    const autoEndTurn = parseInt(String(timerState.auto_end_turn ?? 0), 10);
    // Hidden input: 0 = auto-end enabled, 1 = disabled (matches cookie_auto_end_turn_off / PHP).
    const autoEndEnabled = autoEndTurn === 0;

    // Stuck at or below zero (e.g. already 0 from last tick, or bad state): keep UI at 0; try auto-end once.
    if (turnTimerRemaining <= 0) {
        setTurnTimerState({ remaining: 0 }, 'tick-zero');

        if (autoEndEnabled && !window.FateTide.turnTimerAutoEnding) {
            window.FateTide.turnTimerAutoEnding = true;
            void gmControlsNextTurn();
        }
        return;
    }

    setTurnTimerState({ remaining: newTurnTimerRemaining }, 'tick');

    // Mirror the authoritative value into the GM's own summary badge (no broadcast — that's
    // reserved for discrete control actions; the per-tick value rides nobody's wire).
    ftSyncTurnTimerMirror(newTurnTimerRemaining, 1);

    if (newTurnTimerRemaining > 10) {
        window.FateTide.turnTimerWarningIssued = false;
    } else if (newTurnTimerRemaining > 0 && !window.FateTide.turnTimerWarningIssued) {
        window.FateTide.turnTimerWarningIssued = true;
        log_10_second_warning();
    }

    // Just hit zero this tick (was 1 → 0): optional sfx, then same as clicking Next Turn (not deferred to a second tick).
    if (newTurnTimerRemaining === 0) {
        if (autoEndEnabled) {
            const audio = document.getElementById('audio_player_effect_1') as HTMLAudioElement | null;
            if (audio) {
                const audioSrc =
                    typeof BASE_URL !== 'undefined'
                        ? BASE_URL + 'audio/sfx/D98EC22E-1AE4-47CB-989E-F630F33141A0.mp3'
                        : '';
                if (audioSrc) {
                    audio.src = audioSrc;
                    audio.pause();
                    audio.load();
                    audio.volume = 0.3;
                    void audio.play().catch(() => { /* playback unavailable */ });
                }
            }
        }
        if (autoEndEnabled && !window.FateTide.turnTimerAutoEnding) {
            window.FateTide.turnTimerAutoEnding = true;
            void gmControlsNextTurn();
        }
    }
}


// ===========================================================================================
// gmControlsNextTurn / gmControlsPreviousTurn  (reunited from template.js — roadmap T4.6 part 2,
// 2026-06-17). Kept here alongside runTurnTimerTick so the auto-end path (runTurnTimerTick →
// gmControlsNextTurn, above) is an IN-bundle call rather than a cross-bundle window global. Both are
// window-attached below for the turn-widget gm-controls onclick (gmControlsNextTurn / gmControlsPreviousTurn).
// They call the staying turn-timer cluster (getDefaultCreatureTurnTimerSecondsSync / ftSyncTurnTimerMirror /
// broadcastTurnTimer) + clearLegacySelection / openSideLog / queryDataInputs / ftDebugLog / BASE_URL as globals at runtime.
// ===========================================================================================
async function gmControlsNextTurn() {

    ftDebugLog('combat', { phase: 'nextTurn' });

    // OPEN SIDE LOG
    openSideLog();

	// Next Turn and timer auto-end share the browser's End Turn boundary.
	const encounterId = String(getElementValue('encounter_id') ?? '').toUpperCase();
	const ft = window as unknown as { ftTryCommitEndTurn?: (input: { encounterId: string; actorId: string; rollPrivate?: number }) => Promise<'handled' | 'refused'> };
	const pointer = turnTimerActor(encounterId);
	if (typeof ft.ftTryCommitEndTurn !== 'function') {
		pauseFailedTurnTimer(encounterId, pointer);
		displayWarning('Next Turn browser lane is not loaded.'); return;
	}
	try {
		const outcome = await ft.ftTryCommitEndTurn({ encounterId, actorId: pointer, rollPrivate: 2 });
		if (outcome !== 'handled') {
			pauseFailedTurnTimer(encounterId, pointer);
			if (outcome !== 'refused') displayWarning('Next Turn returned an unknown outcome. Check the turn state and try again.');
		}
	} catch (error) {
		pauseFailedTurnTimer(encounterId, pointer);
		displayWarning('Next Turn could not be committed: ' + String(error));
	}
	finally { cancelTargetDraft('next-turn'); }
	// The existing turn-state observer resets the timer only when the boundary advances.

}

function turnTimerActor(encounterId: string): string {
	return String(encounterField(encounterAdminState().encounterCache?.get(encounterId), 'current_encounter_creature_id_turn') ?? '').toUpperCase();
}

function pauseFailedTurnTimer(encounterId: string, actorId: string): void {
	if (!actorId || turnTimerActor(encounterId) !== actorId.toUpperCase()) return;
	window.FateTide.turnTimerAutoEnding = false;
	const wasRunning = getTurnTimerState().running === 1;
	const state = setTurnTimerState({ running: 0 }, 'next-turn-refused');
	const pause = document.getElementById('btn_pause_turn_timer');
	const resume = document.getElementById('btn_resume_turn_timer');
	if (pause) pause.style.display = 'none';
	if (resume) resume.style.display = 'block';
	ftSyncTurnTimerMirror(state.remaining, 0);
	if (wasRunning) broadcastTurnTimer('next-turn-refused');
}

async function gmControlsPreviousTurn() {

    ftDebugLog('combat', { phase: 'previousTurn' });

    // OPEN SIDE LOG
    openSideLog();

	// Previous Turn is the client-truth lane — the ONLY path (2026-09-30: previous_turn_inc.php is gone). The page decides the step
	// back, the legendary rewind and the round unwrap with its one-round clock pull (janus-engine/combat/turn-navigation.ts); PHP saves.
	// null = a gate / a definite refusal (e.g. "Cannot move before round 1!"), already told to the GM — nothing moved, so stop.
	let truthHandled = false;
	const prevCtx = getSidelogContext();
	try {
		const entry = (window as unknown as { ftTryCommitPreviousTurn?: (input: { campaignId: string; encounterId: string; rollPrivate?: number }) => Promise<'handled' | null> }).ftTryCommitPreviousTurn;
		if (prevCtx && typeof entry === 'function') truthHandled = (await entry({ campaignId: String(prevCtx.campaign_id), encounterId: String(prevCtx.encounter_id), rollPrivate: Number(prevCtx.roll_private ?? 2) })) === 'handled';
		else console.warn('previous turn: the combat-truth lane is not loaded');
	} catch (error) { console.warn('previous turn: client-truth entry threw after a dispatch may have happened', error); truthHandled = true; }
	if (!truthHandled) return;

	// CLEAR TARGETS
	clearLegacySelection();

	// Refresh GM controls
	const defaultSec = getDefaultCreatureTurnTimerSecondsSync();
	const spanRem = document.getElementById('span_creature_turn_timer_remaining');
	if (spanRem) {
		spanRem.innerHTML = String(defaultSec);
	}
	setTurnTimerState({ remaining: defaultSec }, 'previous-turn-reset');
	window.FateTide = window.FateTide || {};
	window.FateTide.turnTimerWarningIssued = false;
	window.FateTide.turnTimerAutoEnding = false;
}


// ===========================================================================================
// LIVE TURN-TIMER MIRROR cluster  (reunited from template.js — roadmap T4.7, 2026-06-17). The whole
// turn-timer subsystem now lives in this bundle: the GM tick/controls (above) + this player-facing
// mirror/broadcast/ticker. Shows the GM's combat turn timer to players in the sidelog encounter summary.
// The GM browser is the authority (runTurnTimerTick drives its own UI); on discrete control actions it
// broadcasts the bare {remaining, running} integers (broadcastTurnTimer → push_turn_timer_inc.php →
// encounter channel). Player clients seed window.FateTide.turnTimerMirror and run a local 1s ticker
// between events. All state is window.FateTide.* (turnTimerMirror / _turnTimerMirrorTicker) — window-
// namespaced, NOT closed-over — so the move is byte-faithful. ftApplyTurnTimerMirror (websocket
// turn-timer-update.ts) + reinitializeTurnTimerUi (side-panels-init.js) are reached as window globals;
// the rest were also globals and stay window-attached as the faithful default.
//   • getDefaultCreatureTurnTimerSecondsSync — default turn seconds from campaignCache / getCampaignArr.
//   • reinitializeTurnTimerUi — reset per-widget warning / auto-end flags after a GM-controls HTML refresh.
//   • ftSyncTurnTimerMirror   — GM-side: mirror the authoritative value into the badge each tick / control
//                               action. Does NOT install a ticker and does NOT broadcast.
//   • ftApplyTurnTimerMirror  — player-side: seed state from an incoming WS frame + ensure the ticker.
//   • ftEnsureTurnTimerMirrorTicker — lazily install the local 1s ticker (idempotent via _turnTimerMirrorTicker).
//   • ftRenderTurnTimerMirrorBadge — poke the badge's value span + running color in place.
//   • broadcastTurnTimer      — GM-only fire-and-forget POST to the broadcast endpoint.
// ===========================================================================================
function getDefaultCreatureTurnTimerSecondsSync() {
    const cid = window.FateTide?.campaign?.id;
    if (!cid) {
        return 90;
    }
    let raw = window.FateTide.campaignCache?.get?.(cid)?.default_creature_turn_timer?.[0];
    if (raw == null || raw === '') {
        raw = window.FateTide.campaignArr?.default_creature_turn_timer?.[0];
    }
    const n = parseInt(raw, 10);
    return !isNaN(n) && n > 0 ? n : 90;
}

function reinitializeTurnTimerUi() {
    window.FateTide = window.FateTide || {};
    window.FateTide.turnTimerWarningIssued = false;
    window.FateTide.turnTimerAutoEnding = false;
}

// Client-truth turns mutate the encounter in place before the scalar websocket echo.
// Observe the shared boundary instead of relying on a later cache comparison or the
// legacy Next Turn tail (the dock's End Turn never reaches that tail).
let observedTurnTimerKey = '';
function observeTurnTimerTurnState(event: Event): void {
    const detail = (event as CustomEvent<{
        encounterId?: string;
        encounterArr?: { encounters?: Record<string, unknown> } & Record<string, unknown>;
        currentTurnId?: string;
        inCombat?: boolean;
        turnPhase?: string;
    }>).detail;
    const activeId = String(encounterAdminHost().ftGetActiveEncounterId?.() || '').trim().toUpperCase();
    const encounterId = String(detail?.encounterId || '').trim().toUpperCase();
    if (!activeId || encounterId !== activeId || !window.FateTide?.user?.isGM) return;

    const encounter = detail?.encounterArr;
    const scalar = (key: string): unknown => {
        const value = encounter?.encounters?.[key] ?? encounter?.[key];
        return Array.isArray(value) ? value[0] : value;
    };
    const pointer = String(detail?.currentTurnId ?? scalar('current_encounter_creature_id_turn') ?? '').trim().toUpperCase();
    const inCombat = detail?.inCombat ?? Number(scalar('in_combat')) === 1;
    const phase = String(detail?.turnPhase ?? scalar('turn_phase') ?? 'main');
    // Round matters when a single combatant starts another turn with the same pointer.
    const key = JSON.stringify([encounterId, inCombat, scalar('current_round'), pointer, phase]);
    if (key === observedTurnTimerKey) return;
    observedTurnTimerKey = key;
    if (!inCombat || !pointer) return;

    const defaultSec = getDefaultCreatureTurnTimerSecondsSync();
    setTurnTimerState({ remaining: defaultSec }, 'encounter-turn-change');
    reinitializeTurnTimerUi();
    const running = getTurnTimerState().running === 1 ? 1 : 0;
    ftSyncTurnTimerMirror(defaultSec, running);
    broadcastTurnTimer('start');
}

for (const name of ['ft:encounter-turn-state', 'ft:encounter-state-received', 'ft:encounter-installed']) {
    window.addEventListener(name, observeTurnTimerTurnState);
}
window.addEventListener('ft:turn-truth-refused', (event: Event) => {
    const detail = (event as CustomEvent<{ command?: { encounter_id?: string; actor_id?: string }; input?: { encounterId?: string; actorId?: string } }>).detail;
    const id = String(detail?.command?.encounter_id ?? detail?.input?.encounterId ?? '').toUpperCase();
    const activeId = String(encounterAdminHost().ftGetActiveEncounterId?.() ?? '').toUpperCase();
    const actorId = String(detail?.command?.actor_id ?? detail?.input?.actorId ?? '').toUpperCase();
    if (id && id === activeId && window.FateTide?.user?.isGM) pauseFailedTurnTimer(id, actorId);
});

function ftRenderTurnTimerMirrorBadge() {
    const m = window.FateTide && window.FateTide.turnTimerMirror;
    if (!m) {
        return;
    }
    const valEl = document.getElementById('span_encounter_summary_turn_timer_value');
    const badge = document.getElementById('span_encounter_summary_turn_timer');
    if (!valEl || !badge) {
        return; // summary not rendered with the badge (out of combat, or not on a sidelog page)
    }
    valEl.textContent = String(Math.max(0, parseInt(m.remaining, 10) || 0));
    const running = !!m.running;
    badge.classList.toggle('bg-warning', running);
    badge.classList.toggle('bg-secondary', !running);
    badge.setAttribute('data-bs-original-title', running ? 'Turn time remaining' : 'Turn timer paused');
    const runIcon = document.getElementById('span_encounter_summary_turn_timer_icon_run');
    const pauseIcon = document.getElementById('span_encounter_summary_turn_timer_icon_pause');
    if (runIcon) {
        runIcon.style.display = running ? '' : 'none';
    }
    if (pauseIcon) {
        pauseIcon.style.display = running ? 'none' : '';
    }
}

function ftSyncTurnTimerMirror(remaining: unknown, running: unknown): void {
    window.FateTide = window.FateTide || {};
    const rem = Math.max(0, parseInt(String(remaining), 10) || 0);
    window.FateTide.turnTimerMirror = {
        remaining: rem,
        running: running ? 1 : 0,
        encounterId: encounterAdminHost().ftGetActiveEncounterId?.() || window.FateTide.campaign?.encounterId || '',
    };
    ftRenderTurnTimerMirrorBadge();
}

function ftEnsureTurnTimerMirrorTicker() {
    window.FateTide = window.FateTide || {};
    if (window.FateTide._turnTimerMirrorTicker) {
        return;
    }
    window.FateTide._turnTimerMirrorTicker = setInterval(function () {
        const m = window.FateTide && window.FateTide.turnTimerMirror;
        if (!m || !m.running) {
            return;
        }
        const rem = parseInt(m.remaining, 10) || 0;
        if (rem <= 0) {
            return; // hold at 0 — the GM advances the turn; a new frame will re-seed
        }
        m.remaining = rem - 1;
        ftRenderTurnTimerMirrorBadge();
    }, 1000);
}

function ftApplyTurnTimerMirror(remaining: unknown, running: unknown, encounterId: unknown): void {
    window.FateTide = window.FateTide || {};
    window.FateTide.turnTimerMirror = {
        remaining: Math.max(0, parseInt(String(remaining), 10) || 0),
        running: running ? 1 : 0,
        encounterId: String(encounterId || encounterAdminHost().ftGetActiveEncounterId?.() || ''),
    };
    ftEnsureTurnTimerMirrorTicker();
    // Badge may not exist yet (combat just started, or summary last rendered before any value was
    // known). If its home container is present, re-render the summary so the badge appears; otherwise
    // just poke the value span.
    if (!document.getElementById('span_encounter_summary_turn_timer')) {
        const activeEncounterId = String(encounterAdminHost().ftGetActiveEncounterId?.() || '').trim();
        if (
            activeEncounterId
            &&
            document.getElementById('div_refresh_encounter_state')
            && typeof getEncounterSettingSummary === 'function'
        ) {
            getEncounterSettingSummary();
        }
    } else {
        ftRenderTurnTimerMirrorBadge();
    }
}

function broadcastTurnTimer(reason: unknown): void {
    // GM-only live mirror push. Fire-and-forget: never blocks the GM's own timer UI, never
    // overwrites a sidelog div. Called from discrete control actions only — NEVER runTurnTimerTick.
    if (!(window.FateTide && window.FateTide.user && window.FateTide.user.isGM)) {
        return;
    }
    const context = encounterAdminHost().ftGetEncounterContext?.() || getSidelogContext() || {};
    const campaignId = context.campaign_id || encounterAdminHost().ftGetCampaignId?.() || window.FateTide.campaign?.id || '';
    const encounterId = context.encounter_id || encounterAdminHost().ftGetActiveEncounterId?.() || '';
    if (!campaignId || !encounterId) {
        return;
    }
    const timerState = getTurnTimerState();
    const rem = parseInt(String(timerState.remaining ?? 0), 10);
    const running = timerState.running === 1 ? 1 : 0;
    try {
        fetch(`${BASE_URL}options/game/turns/push_turn_timer_inc.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                campaign_id: campaignId,
                encounter_id: encounterId,
                remaining: Number.isNaN(rem) ? 0 : rem,
                running: running,
                reason: reason || 'sync',
            }),
            keepalive: true,
        }).catch(() => {});
    } catch (e) {
        /* fire-and-forget */
    }
}


// ===========================================================================================
// awardExperienceMarks  (carved from template.js 6029-6046)
// ===========================================================================================
async function awardExperienceMarks() {
	
	openSideLog(); // OPEN SIDE LOG
	(document.getElementById('pills_combat_log_tab') as HTMLInputElement).click(); // CLICK THE LOG TAB	
	
	const sl_context = getSidelogContext();
	if (!sl_context) return;
	const awardInput = document.getElementById('award_experience_marks') as HTMLInputElement | null;
	const endpoint = BASE_URL + 'options/tools/campaigns/award_experience_marks_inc.php';
	try {
		const response = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ mode: 'read', campaign_id: sl_context.campaign_id }), signal: AbortSignal.timeout(15000) });
		if (!response.ok) throw new Error('The party could not be read for this award.');
		const facts = await response.json() as { players: unknown[] };
		const rows = planExperienceAward(facts.players, awardInput?.value ?? '');
		const participants: unknown[] = [];
		for (const row of rows) {
			await window.fetchAndCacheCaster?.(row.item_id);
			const caster = window.FateTide?.casterCache?.get(row.item_id);
			if (caster) participants.push(caster);
		}
		await queryData(endpoint, 'div_sidelog_warning', withLogParticipants({ rows,
			campaign_id: sl_context.campaign_id, encounter_id: sl_context.encounter_id }, participants));
	} catch (error) {
		displayWarning(error instanceof Error ? error.message : 'The experience award could not be saved.');
	}
		
}

function wireCampaignToolActions(): void {
    markCampaignHouseRuleGroups();
    document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement | null;
        const awardButton = target?.closest<HTMLElement>('[data-ft-campaign-action="award-experience-marks"]');
        if (awardButton) {
            void awardExperienceMarks();
            return;
        }

        const houseRuleInput = target?.closest<HTMLInputElement>('[data-ft-campaign-action="toggle-house-rule"]');
        if (!houseRuleInput) return;

        void saveCampaignHouseRuleToggle(houseRuleInput);
    });
}


// ===========================================================================================
// viewEncounterLinks  (carved from template.js 6078-6109)
// ===========================================================================================
async function viewEncounterLinks(){
	
	clearLegacySelection();

	const sl_context = getSidelogContext();
	if (!sl_context) return;

	const {
		campaign_id,
		encounter_id,
	} = sl_context;

	const rules = {
		encounter_id: { type: 'uuid', value: encounter_id },
		campaign_id: { type: 'uuid', value: campaign_id },
	};	
	const validationResult = validateContext(rules, 'viewEncounterLinks');
	if (!validationResult.valid) {
        console.error(validationResult.error);
        return;
    }		

	queryData(
		BASE_URL + "options/tools/encounters/gm_load_encounter/form_query_campaign_encounter_links_inc.php", 
		'div_refresh_gm_viewer', 
		{
			campaign_id,
			encounter_id,
		}
	);

}


// ===========================================================================================
// viewPublicEncounters  (carved from template.js 6114-6142)
// ===========================================================================================
async function viewPublicEncounters(){
	
	clearLegacySelection();

	const sl_context = getSidelogContext();
	if (!sl_context) return;

	const {
		campaign_id,
	} = sl_context;

	const rules = {
		campaign_id: { type: 'uuid', value: campaign_id },
	};	
	const validationResult = validateContext(rules, 'viewPublicEncounters');
	if (!validationResult.valid) {
        console.error(validationResult.error);
        return;
    }		

	queryData(
		BASE_URL + "options/tools/encounters/gm_load_encounter/form_query_campaign_public_encounters_inc.php", 
		'div_refresh_gm_viewer', 
		{
			campaign_id,
		}
	);	
	
}


// ===========================================================================================
// viewPrivateEncounters  (carved from template.js 6147-6175)
// ===========================================================================================
async function viewPrivateEncounters(){
	
	clearLegacySelection();

	const sl_context = getSidelogContext();
	if (!sl_context) return;

	const {
		campaign_id,
	} = sl_context;

	const rules = {
		campaign_id: { type: 'uuid', value: campaign_id },
	};	
	const validationResult = validateContext(rules, 'viewPrivateEncounters');
	if (!validationResult.valid) {
        console.error(validationResult.error);
        return;
    }		

	queryData(
		BASE_URL + "options/tools/encounters/gm_load_encounter/form_query_campaign_private_encounters_inc.php", 
		'div_refresh_gm_viewer', 
		{
			campaign_id,
		}
	);	
	
}


// ===========================================================================================
// viewAllEncounters  (carved from template.js 6180-6207)
// ===========================================================================================
async function viewAllEncounters(){
    clearLegacySelection();

    const sl_context = getSidelogContext();
    if (!sl_context) return;

    const { campaign_id } = sl_context;

    const rules = {
        campaign_id: { type: 'uuid', value: campaign_id },
    };	

    const validationResult = validateContext(rules, 'viewAllEncounters');
    if (!validationResult.valid) {
        console.error(validationResult.error);
        return;
    }		

    ftDebugLog('viewAllEncounters', { campaign_id });

    queryData(
        BASE_URL + "options/tools/encounters/gm_load_encounter/form_query_campaign_encounters_inc.php", 
        'div_refresh_gm_viewer', 
        { 
			campaign_id,
		}
    );	
}


// ===========================================================================================
// copyAdventureToMasterParentID  (carved from template.js 6245-6260)
// ===========================================================================================
async function copyAdventureToMasterParentID(adventure_id: string, input_master_parent_id: string): Promise<void> {

	openSideLog();

	const master_parent_id = (document.getElementById(input_master_parent_id) as HTMLInputElement).value;

	await queryDataWithProgress(
		`${BASE_URL}options/tools/adventures/copy_adventure_to_master_parent_id_inc.php`,
		'div_sidelog_warning',
		{
			adventure_id,
			master_parent_id,
		},
		{ label: 'Copying adventure...' },
	);
}


// ===========================================================================================
// copySelectedLocationsToMasterParentID  (carved from template.js 6264-6302)
// ===========================================================================================
async function copySelectedLocationsToMasterParentID(input_master_parent_id: string): Promise<void> {
	openSideLog();

	const master_parent_id = (document.getElementById(input_master_parent_id) as HTMLInputElement).value;
	if (!master_parent_id) {
		(document.getElementById('div_sidelog_warning') as HTMLInputElement).innerHTML =
			'<h5 class="bg-warning text-dark rounded border border-2 border-danger p-3 my-2">Select a destination (campaign, world, or adventure) first.</h5>';
		return;
	}

	const container = (document.getElementById('div_refresh_location') as HTMLInputElement);
	if (!container) {
		console.error('copySelectedLocationsToMasterParentID: div_refresh_location not found');
		return;
	}

	const location_ids: string[] = [];
	container.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
		if (cb.id) {
			location_ids.push(cb.id);
		}
	});

	if (location_ids.length === 0) {
		(document.getElementById('div_sidelog_warning') as HTMLInputElement).innerHTML =
			'<h5 class="bg-warning text-dark rounded border border-2 border-danger p-3 my-2">Select at least one location using the checkboxes.</h5>';
		return;
	}

	await queryDataWithProgress(
		`${BASE_URL}options/tools/locations/copy_selected_template_locations_to_master_parent_id_inc.php`,
		'div_sidelog_warning',
		{
			master_parent_id,
			location_ids,
		},
		{ label: 'Copying template locations...' },
	);
}


// ===========================================================================================
// moveSelectedToAdventure — nested adventures (2026-07-21): re-point checkbox-selected
// locations OR encounters from the current adventure to a family adventure (sub-adventure
// or ancestor). Checkbox harvest contract: input id = the row's UUID, scoped to containerId.
// ===========================================================================================
async function moveSelectedToAdventure(
	source_adventure_id: string,
	input_destination_id: string,
	kind: 'locations' | 'encounters',
	container_id: string,
): Promise<void> {
	openSideLog();

	const destination_id = (document.getElementById(input_destination_id) as HTMLInputElement | null)?.value || '';
	if (!destination_id) {
		(document.getElementById('div_sidelog_warning') as HTMLInputElement).innerHTML =
			'<h5 class="bg-warning text-dark rounded border border-2 border-danger p-3 my-2">Select a destination adventure first.</h5>';
		return;
	}

	const container = document.getElementById(container_id);
	if (!container) {
		console.error(`moveSelectedToAdventure: ${container_id} not found`);
		return;
	}

	const selected_ids: string[] = [];
	container.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
		if (cb.id) {
			selected_ids.push(cb.id);
		}
	});

	if (selected_ids.length === 0) {
		(document.getElementById('div_sidelog_warning') as HTMLInputElement).innerHTML =
			`<h5 class="bg-warning text-dark rounded border border-2 border-danger p-3 my-2">Select at least one ${kind === 'locations' ? 'location' : 'encounter'} using the checkboxes.</h5>`;
		return;
	}

	await queryDataWithProgress(
		`${BASE_URL}options/tools/adventures/move_selected_to_adventure_inc.php`,
		'div_sidelog_warning',
		{
			source_adventure_id,
			destination_id,
			location_ids: kind === 'locations' ? selected_ids : [],
			encounter_ids: kind === 'encounters' ? selected_ids : [],
		},
		{ label: 'Moving to adventure...', reloadOnSuccess: true },
	);
}


// ===========================================================================================
// deleteAllLocationsExceptCampsiteAndStronghold  (carved from template.js 6313-6328)
// ===========================================================================================
async function deleteAllLocationsExceptCampsiteAndStronghold(campaign_id: string): Promise<void> {
	openSideLog();

	await queryDataWithProgress(
		`${BASE_URL}options/tools/locations/delete/soft_delete_all_locations_except_campaign_campsite_and_campaign_stronghold.php`,
		'div_sidelog_warning',
		{
			campaign_id,
			location_master_parent_id: campaign_id,
		},
		{
			label: 'Deleting all campaign locations...',
			reloadOnSuccess: true,
		},
	);
}


// ===========================================================================================
// linkPlayerToCampaign  (carved from template.js 6336-6374)
// ===========================================================================================
async function linkPlayerToCampaign(campaign_id: string): Promise<void> {
    
	var player_item_id = (document.getElementById("link_player_character") as HTMLInputElement).value;

	var div_refresh = "div_refresh_link_player";
	const prepare = (window as unknown as { ftPrepareRelocationRequest?: (url: string, data: Record<string, unknown>) => Promise<boolean> }).ftPrepareRelocationRequest;
	const linkRequest: Record<string, unknown> = { campaign_id, player_item_id };
	if (!prepare || !await prepare(BASE_URL + 'options/tools/player_characters/link_player_character_inc.php', linkRequest)) return;

    if (div_refresh) {
        (document.getElementById(div_refresh) as HTMLInputElement).innerHTML = loader;
    }

    try {
        const response = await fetch(BASE_URL + "options/tools/player_characters/link_player_character_inc.php", {
            method: 'POST',
            headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(linkRequest)
        });

        const result = await response.text();

        // APPEND TO TABLE
        if (div_refresh) {
            (document.getElementById(div_refresh) as HTMLInputElement).innerHTML = result;
        }

        // LOAD STATIC SCRIPTS
        await loadPostFunctions();
		
		// REDIRECT TO SHEET
		redirectURL(BASE_URL + "item.php?id=" + player_item_id);
		
    } catch (error) {
        console.error("Error in linkPlayerToCampaign:", error);
    }
}


// ===========================================================================================
// toggleCampaignRunning  (carved from template.js 6380-6400)
// ===========================================================================================
async function toggleCampaignRunning(campaign_id: string, campaign_end_date: string): Promise<void> {
	
	// Call the queryData function and wait for it to finish
	await queryData(
		BASE_URL + "options/tools/campaigns/update/update_campaign_running_inc.php",
		'div_sidelog_warning',
		{ 
			campaign_id, 
			campaign_end_date,
		}
	);
	
	// Update UI based on the campaign state
	const startBtn = (document.getElementById('btn_toggle_campaign_start_' + campaign_id) as HTMLInputElement);
	if (startBtn) startBtn.style.display = 'none';
	const stopBtn = (document.getElementById('btn_toggle_campaign_stop_' + campaign_id) as HTMLInputElement);
	if (stopBtn) stopBtn.style.display = '';

	// Refresh the page after the queryData function completes
	window.location.replace(BASE_URL + "my_campaigns.php");
}


// ===========================================================================================
// toggleTurnTimer  (carved from template.js 6515-6546)
// ===========================================================================================
async function toggleTurnTimer(isResuming: boolean): Promise<void> {
	const timerState = getTurnTimerState();
	const remNow = parseInt(String(timerState.remaining ?? 0), 10) || 0;
	if (isResuming) {
		(document.getElementById('btn_resume_turn_timer') as HTMLInputElement).style.display = 'none';
		(document.getElementById('btn_pause_turn_timer') as HTMLInputElement).style.display = 'block';
		setTurnTimerState({ running: 1 }, 'resume');
		ftSyncTurnTimerMirror(remNow, 1);
		broadcastTurnTimer('resume');
		await queryDataInputs(
			`${BASE_URL}options/game/log/log_turn_timer_resumed_inc.php`,
			'div_sidelog_warning',
			{
				campaign_id: 'campaign_id',
				encounter_id: 'encounter_id',
			}
		);
	} else {
		(document.getElementById('btn_pause_turn_timer') as HTMLInputElement).style.display = 'none';
		(document.getElementById('btn_resume_turn_timer') as HTMLInputElement).style.display = 'block';
		setTurnTimerState({ running: 0 }, 'pause');
		ftSyncTurnTimerMirror(remNow, 0);
		broadcastTurnTimer('pause');
		await queryDataInputs(
			`${BASE_URL}options/game/log/log_turn_timer_paused_inc.php`,
			'div_sidelog_warning',
			{
				campaign_id: 'campaign_id',
				encounter_id: 'encounter_id',
			}
		);
	}
}


// ===========================================================================================
// resetTurnTimer  (carved from template.js 6551-6566)
// ===========================================================================================
function resetTurnTimer() {
    const defaultTurnTimer = getDefaultCreatureTurnTimerSecondsSync();
    setTurnTimerState({ remaining: defaultTurnTimer }, 'reset');
    window.FateTide = window.FateTide || {};
    window.FateTide.turnTimerWarningIssued = false;
    const runningNow = getTurnTimerState().running === 1 ? 1 : 0;
    ftSyncTurnTimerMirror(defaultTurnTimer, runningNow);
    broadcastTurnTimer('reset');
    // A reset changes every player's clock, so it leaves a log row + cue like pause/resume (owner 2026-10-01).
    void queryDataInputs(
        `${BASE_URL}options/game/log/log_turn_timer_reset_inc.php`,
        'div_sidelog_warning',
        {
            campaign_id: 'campaign_id',
            encounter_id: 'encounter_id',
        }
    );
}


// ===========================================================================================
// addTurnTimerSeconds  (carved from template.js 6571-6598)
// ===========================================================================================
async function addTurnTimerSeconds(seconds: number): Promise<void> {
	const spanRem = (document.getElementById('span_creature_turn_timer_remaining') as HTMLInputElement);
	if (!spanRem) {
		return;
	}
	const timerState = getTurnTimerState();
	const cur = parseInt(String(timerState.remaining ?? 0), 10);
	const safeCur = isNaN(cur) ? 0 : cur;
	const next = safeCur + seconds;
	setTurnTimerState({ remaining: next }, 'add-seconds');
	if (next > 10) {
		window.FateTide = window.FateTide || {};
		window.FateTide.turnTimerWarningIssued = false;
	}
	const runningNow = getTurnTimerState().running === 1 ? 1 : 0;
	ftSyncTurnTimerMirror(next, runningNow);
	broadcastTurnTimer('add');
	await queryData(
		`${BASE_URL}options/game/log/log_gm_added_turn_time_inc.php`,
		'div_sidelog_warning',
		{
			campaign_id: encounterAdminHost().ftGetCampaignId?.() ?? '',
			encounter_id: encounterAdminHost().ftGetActiveEncounterId?.() ?? '',
			seconds_added: seconds,
		}
	);
}


// ===========================================================================================
// log_15_second_warning  (carved from template.js 6604-6614)
// ===========================================================================================
async function log_15_second_warning() {
	
	queryDataInputs(
		`${BASE_URL}options/game/log/log_15_second_warning_inc.php`, 
		'div_sidelog_warning', 
		{
			campaign_id: 'campaign_id',
			encounter_id: 'encounter_id',
		}
	);
}


// ===========================================================================================
// log_10_second_warning  (carved from template.js 6621-6631)
// ===========================================================================================
async function log_10_second_warning() {
	
	queryDataInputs(
		`${BASE_URL}options/game/log/log_10_second_warning_inc.php`, 
		'div_sidelog_warning', 
		{
			campaign_id: 'campaign_id',
			encounter_id: 'encounter_id',
		}
	);
}

async function deleteModifyEncounterOrphanLocation(): Promise<void> {
    const wrap = document.getElementById('modify_encounter_orphan_location_wrap');
    const locationId = wrap?.dataset.orphanLocationId || '';
    const masterParentEl = document.getElementById('encounter_master_parent_id') as HTMLInputElement | null;
    const masterParent = masterParentEl ? String(masterParentEl.value || '').trim() : '';
    if (!locationId || !masterParent) return;

    await queryData(`${BASE_URL}options/tools/locations/delete/delete_empty_location_inc.php`, 'div_sidelog_warning', {
        location_id: locationId,
        encounter_master_parent_id: masterParent,
    });

    const sidelog = document.getElementById('div_sidelog_warning');
    const ok = sidelog && sidelog.querySelector('.bg-success');
    if (!ok || !wrap) return;

    const locSel = document.getElementById('encounter_location_id') as (HTMLSelectElement & {
        tomselect?: { removeOption(value: string): void };
    }) | null;
    if (locSel?.tomselect && typeof locSel.tomselect.removeOption === 'function') {
        try {
            locSel.tomselect.removeOption(locationId);
        } catch (e) {
            console.warn(e);
        }
    }
    const optDead = locSel ? locSel.querySelector(`option[value="${locationId}"]`) : null;
    optDead?.remove();
    wrap.style.display = 'none';
    wrap.hidden = true;
    delete wrap.dataset.orphanLocationId;
}

// ===========================================================================================
// NEW ENCOUNTER — the pre-flight form (options/tools/encounters/forms/form_create_encounter_inc.php).
// Owner's model (2026-09-06): an encounter is not upserted, and nothing renders, until its basics
// exist — name, location, environment, weather. The form lives in the Forms host (openSideForm) for
// ANY master parent; on success a campaign shell loads the new encounter in place and opens Edit
// Scene, anything else navigates to the encounter's authoring surface (open_url from the server).
// ===========================================================================================
const CREATE_ENCOUNTER_ARM_KEY = 'ft3d.editScene.armOnLoad';
// Defaults requests are sequenced: only the newest response may touch the form, and the submit stays
// disabled while one is in flight (review 2026-09-06: a slower earlier response could otherwise land
// on a later location choice, or on a reopened form).
let createEncounterDefaultsSeq = 0;
let createEncounterDefaultsPending = false;
let createEncounterDefaults: { formToken: string; parent: string; location: string; value: EncounterDefaults } | null = null;

function createEncounterCurrentDefaults(): EncounterDefaults | null {
    return createEncounterDefaults?.formToken === createEncounterSelectValue('create_encounter_id')
        && createEncounterDefaults.parent === createEncounterSelectValue('create_encounter_master_parent_id')
        && createEncounterDefaults.location === createEncounterSelectValue('create_encounter_location_id') ? createEncounterDefaults.value : null;
}

function createEncounterField<T extends HTMLElement>(id: string): T | null {
    return document.getElementById(id) as T | null;
}

function createEncounterSelectValue(id: string): string {
    const select = createEncounterField<HTMLSelectElement>(id);
    return select ? String(select.value || '').trim() : '';
}

function createEncounterSetSelectValue(id: string, value: string): void {
    const select = createEncounterField<HTMLSelectElement & {
        tomselect?: {
            setValue(v: string | string[], silent?: boolean): void;
            addOption(o: Record<string, unknown>): void;
            clear(silent?: boolean): void;
            options?: Record<string, unknown>;
        };
    }>(id);
    if (!select) return;
    const next = String(value || '').trim();
    if (select.tomselect) {
        // Register a missing option with Tom Select FIRST so its registry, its display and the native
        // select (which supplies the payload) agree; an empty value clears the control.
        try {
            if (next && !(select.tomselect.options && next in select.tomselect.options)) {
                select.tomselect.addOption({ value: next, text: next });
            }
            if (next) select.tomselect.setValue(next, true);
            else select.tomselect.clear(true);
        } catch { /* fall through to the native select */ }
    }
    if (next && !Array.from(select.options).some((option) => option.value === next)) {
        const option = document.createElement('option');
        option.value = next;
        option.textContent = next;
        select.append(option);
    }
    select.value = next;
}

function createEncounterSyncSubmit(): void {
    const form = document.querySelector<HTMLElement>('[data-ft-create-encounter-form]');
    if (!form) return;
    const submit = form.querySelector<HTMLButtonElement>('[data-ft-encounter-action="create-encounter-submit"]');
    if (!submit) return;
    const name = String(createEncounterField<HTMLInputElement>('create_encounter_name')?.value || '').trim();
    const ready = name !== ''
        && createEncounterSelectValue('create_encounter_location_id') !== ''
        && createEncounterSelectValue('create_encounter_environment') !== ''
        && createEncounterSelectValue('create_encounter_weather') !== '';
    submit.disabled = !ready || submit.dataset.ftBusy === '1' || createEncounterDefaultsPending;
}

function createEncounterStatus(message: string, tone: 'muted' | 'warning' | 'success' = 'muted'): void {
    const status = createEncounterField<HTMLElement>('create_encounter_status');
    if (!status) return;
    status.className = 'small mb-2 text-' + tone;
    status.textContent = message;
}

async function createEncounterApplyLocationDefaults(): Promise<void> {
    const masterParent = String(createEncounterField<HTMLInputElement>('create_encounter_master_parent_id')?.value || '').trim();
    const locationId = createEncounterSelectValue('create_encounter_location_id');
    const seq = ++createEncounterDefaultsSeq;
    createEncounterDefaults = null;
    createEncounterDefaultsPending = false;
    if (!masterParent || !locationId) { createEncounterSyncSubmit(); return; }
    const formToken = createEncounterField<HTMLInputElement>('create_encounter_id')?.value || '';
    const stale = (): boolean => seq !== createEncounterDefaultsSeq
        || (createEncounterField<HTMLInputElement>('create_encounter_id')?.value || '') !== formToken
        || createEncounterSelectValue('create_encounter_master_parent_id') !== masterParent
        || createEncounterSelectValue('create_encounter_location_id') !== locationId;
    const before = { environment: createEncounterSelectValue('create_encounter_environment'),
        inside: createEncounterField<HTMLInputElement>('create_encounter_inside')?.checked,
        zone: createEncounterSelectValue('create_encounter_zone_number') };
    createEncounterDefaultsPending = true;
    createEncounterSyncSubmit();
    createEncounterStatus('Reading the location\'s defaults…');
    try {
        const response = await fetch(BASE_URL + 'options/tools/encounters/load_encounter_defaults.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ encounter_master_parent_id: masterParent, encounter_location_id: locationId, raw_sibling_zones: true }),
        });
        if (!response.ok) throw new Error('Could not read the location\'s defaults; select the location again to retry.');
        const raw: unknown = await response.json();
        if (stale()) return; // a newer location (or a reopened form) owns the fields now
        const defaults = await encounterDefaults(raw, masterParent, locationId);
        if (stale()) return;
        createEncounterDefaults = { formToken, parent: masterParent, location: locationId, value: defaults };
        const environment = defaults.environment, weather = defaults.weather;
        // The environment is DERIVED from the location: a location that supplies none clears the
        // previous location's value rather than letting it satisfy the submit guard by inheritance.
        if (createEncounterSelectValue('create_encounter_environment') === before.environment)
            createEncounterSetSelectValue('create_encounter_environment', environment);
        if (weather && !createEncounterSelectValue('create_encounter_weather')) createEncounterSetSelectValue('create_encounter_weather', weather);
        const inside = createEncounterField<HTMLInputElement>('create_encounter_inside');
        if (inside && inside.checked === before.inside) inside.checked = defaults.inside;
        const zone = createEncounterField<HTMLInputElement>('create_encounter_zone_number');
        if (zone && zone.value === before.zone) {
            zone.value = String(defaults.zone ?? '');
        }
        createEncounterStatus(environment ? 'Defaults applied from the location.' : 'This location type sets no environment; pick one.', environment ? 'muted' : 'warning');
    } catch (error) {
        if (!stale()) createEncounterStatus(error instanceof Error ? error.message : 'Could not read the location\'s defaults; select the location again to retry.', 'warning');
    } finally {
        if (seq === createEncounterDefaultsSeq) createEncounterDefaultsPending = false;
        createEncounterSyncSubmit();
    }
}

function createEncounterOfferOpen(encounterId: string, openUrl: string, message: string): void {
    const status = createEncounterField<HTMLElement>('create_encounter_status');
    if (!status) return;
    status.className = 'small mb-2 text-warning';
    status.textContent = message + ' ';
    const link = document.createElement('a');
    link.className = 'link-primary fw-semibold';
    link.href = openUrl || (BASE_URL + 'encounter.php?scene=edit&encounter_id=' + encodeURIComponent(encounterId));
    link.textContent = 'Open the encounter';
    status.append(link);
}

async function createEncounterSubmit(submit: HTMLButtonElement): Promise<void> {
    if (submit.dataset.ftBusy === '1') return;
    const defaults = createEncounterCurrentDefaults();
    if (createEncounterDefaultsPending) {
        createEncounterStatus('Wait for the location\'s defaults before creating the encounter.', 'warning');
        return;
    }
    const form = submit.closest<HTMLElement>('[data-ft-create-encounter-form]');
    const payload = {
        ...(defaults ? encounterDefaultSaveFields(defaults) : {}),
        encounter_id: String(createEncounterField<HTMLInputElement>('create_encounter_id')?.value || '').trim(),
        encounter_master_parent_id: String(createEncounterField<HTMLInputElement>('create_encounter_master_parent_id')?.value || '').trim(),
        encounter_name: String(createEncounterField<HTMLInputElement>('create_encounter_name')?.value || '').trim(),
        encounter_location_id: createEncounterSelectValue('create_encounter_location_id'),
        encounter_environment: createEncounterSelectValue('create_encounter_environment'),
        encounter_weather: createEncounterSelectValue('create_encounter_weather'),
        encounter_inside: createEncounterField<HTMLInputElement>('create_encounter_inside')?.checked ? 1 : 0,
        encounter_zone_number: String(createEncounterField<HTMLInputElement>('create_encounter_zone_number')?.value || '').trim(),
    };
    if (!payload.encounter_master_parent_id || !payload.encounter_name || !payload.encounter_location_id || !payload.encounter_environment || !payload.encounter_weather) {
        createEncounterStatus('Name, location, environment and weather are required.', 'warning');
        createEncounterSyncSubmit();
        return;
    }
    submit.dataset.ftBusy = '1';
    submit.disabled = true;
    createEncounterStatus('Creating…');
    try {
        // Keep the actual control values as the preimage across both asynchronous reads.
        const selectedZone = payload.encounter_zone_number;
        let zone = selectedZone;
        if (zone === '') {
            if (defaults) zone = String(defaults.zone);
            else {
                const response = await fetch(BASE_URL + 'options/tools/encounters/load_encounter_defaults.php', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
                    body: JSON.stringify({ encounter_master_parent_id: payload.encounter_master_parent_id,
                        encounter_location_id: payload.encounter_location_id, sibling_zones_only: true }),
                });
                if (!response.ok) throw new Error('Encounter zone inputs unavailable. Please retry.');
                zone = String(encounterCreateZone(await response.json(), payload.encounter_master_parent_id, payload.encounter_location_id));
            }
        }
        const refusal = await encounterCreateCatalogRefusal(payload.encounter_environment, payload.encounter_weather);
        if (form !== document.querySelector('[data-ft-create-encounter-form]')
            || (['id', 'master_parent_id', 'name', 'location_id', 'environment', 'weather', 'zone_number'] as const).some(field =>
                createEncounterSelectValue(`create_encounter_${field}`) !== (field === 'zone_number' ? selectedZone : payload[`encounter_${field}`]))
            || (createEncounterField<HTMLInputElement>('create_encounter_inside')?.checked ? 1 : 0) !== payload.encounter_inside) {
            createEncounterStatus('Selections changed; create the encounter again.', 'warning');
            return;
        }
        if (refusal) { createEncounterStatus(refusal.message, 'warning'); return; }
        payload.encounter_zone_number = zone;
        const response = await fetch(BASE_URL + 'options/tools/encounters/create_encounter_inc.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => null) as
            { ok?: boolean; encounter_id?: string; parent_type?: string; open_url?: string; message?: string; error?: string } | null;
        if (!result?.ok || !result.encounter_id) {
            createEncounterStatus(result?.message || result?.error || ('Could not create the encounter (HTTP ' + response.status + ').'), 'warning');
            return;
        }
        createEncounterStatus('Created. Opening…', 'success');
        const host = encounterAdminHost() as Window & EncounterAdminHost & {
            gmLoadCampaignEncounter?: (encounterId: string) => Promise<void>;
            ftSetFt3dEditMode?: (enabled: boolean) => void;
        };
        const inCampaignShell = result.parent_type === 'campaign'
            && !!host.document.querySelector('[data-ft3d-edit-mode]')
            && typeof host.gmLoadCampaignEncounter === 'function'
            && typeof host.ftSetFt3dEditMode === 'function';
        // Creation succeeded from here on: the id is minted, the row exists. Loading it is a SEPARATE
        // step whose failure must not read as "create failed" (a retry would hit the 409 guard).
        submit.dataset.ftCreated = result.encounter_id;
        if (inCampaignShell) {
            // Arm Edit Scene for the encounter the frame is about to load; my_campaign.php reads this
            // on the scene-ready report, enters edit mode once, and clears it.
            try { window.sessionStorage.setItem(CREATE_ENCOUNTER_ARM_KEY, result.encounter_id.toUpperCase()); } catch { /* per-tab nicety */ }
            try {
                await host.gmLoadCampaignEncounter!(result.encounter_id);
                if (form) form.hidden = true;
            } catch (error) {
                try { window.sessionStorage.removeItem(CREATE_ENCOUNTER_ARM_KEY); } catch { /* per-tab nicety */ }
                createEncounterOfferOpen(result.encounter_id, result.open_url || '', 'Created, but it could not be loaded here: ' + String((error as Error)?.message || error));
            }
            return;
        }
        if (result.open_url) window.location.href = result.open_url;
    } catch (error) {
        if (submit.dataset.ftCreated) {
            createEncounterOfferOpen(submit.dataset.ftCreated, '', 'Created, but opening it failed: ' + String((error as Error)?.message || error));
        } else {
            createEncounterStatus('Could not create the encounter: ' + String((error as Error)?.message || error), 'warning');
        }
    } finally {
        delete submit.dataset.ftBusy;
        createEncounterSyncSubmit();
    }
}

function wireModifyEncounterPageActions(): void {
    // The pre-flight create form: enable the button only once the basics are present.
    document.addEventListener('input', (event) => {
        if ((event.target as Element | null)?.closest('[data-ft-create-encounter-form]')) createEncounterSyncSubmit();
    });
    document.addEventListener('change', (event) => {
        if ((event.target as Element | null)?.closest('[data-ft-create-encounter-form]')) createEncounterSyncSubmit();
    });

    document.addEventListener('click', (event) => {
        const trigger = (event.target as Element | null)?.closest<HTMLElement>('[data-ft-encounter-action]');
        if (!trigger) return;
        const action = trigger.dataset.ftEncounterAction || '';

        if (action === 'create-encounter-submit') {
            void createEncounterSubmit(trigger as HTMLButtonElement);
            return;
        }

        if (action === 'open-item-info') {
            const itemId = trigger.dataset.ftItemId || '';
            if (itemId) openItemInfo(itemId);
            return;
        }
        if (action === 'copy-inputs') {
            const ids = (trigger.dataset.ftInputIds || '').split(',').map((id) => id.trim()).filter(Boolean);
            const [first, ...rest] = ids;
            if (first) copyInputsToClipboard(first, ...rest);
            return;
        }
        if (action === 'ambience-defaults') {
            void applyEncounterAmbienceFromLocationTypeDefaults(BASE_URL);
            return;
        }
        if (action === 'ambience-clear-row') {
            void clearEncounterAmbienceRow(trigger.dataset.ftAmbiencePrefix || '', BASE_URL);
            return;
        }
        if (action === 'ambience-play') {
            const prefix = trigger.dataset.ftAmbiencePrefix || '';
            openSideLog();
            clickEl('pills-soundboard-tab');
            playAudioFileFromInput1(`${prefix}_id`);
            return;
        }
        if (action === 'modify-encounter') {
            void modifyEncounter();
            return;
        }
        if (action === 'clear-caster') {
            clearCaster();
            return;
        }
        if (action === 'delete-orphan-location') {
            void deleteModifyEncounterOrphanLocation();
        }
    });

    document.addEventListener('change', (event) => {
        const select = (event.target as Element | null)?.closest<HTMLSelectElement>('[data-ft-encounter-change]');
        if (!select) return;
        const action = select.dataset.ftEncounterChange || '';
        const prefix = select.dataset.ftAmbiencePrefix || '';

        if (action === 'create-encounter-location') {
            void createEncounterApplyLocationDefaults();
            return;
        }
        if (action === 'ambience-location-type') {
            if (select.value) void applyEncounterAmbienceFromLocationTypeId(select.value, BASE_URL);
            return;
        }
        if (action === 'ambience-category') {
            void queryData2Input1(
                `${BASE_URL}options/forms/select_query_audio_subcategories_inc.php`,
                `div_refresh_audio_subcategory_${prefix}`,
                'amb',
                prefix,
                `${prefix}_category`,
            );
            return;
        }
        if (action === 'ambience-subcategory') {
            void queryData2Input2(
                `${BASE_URL}options/forms/select_query_audio_names_inc.php`,
                `div_refresh_audio_names_${prefix}`,
                'amb',
                prefix,
                `${prefix}_category`,
                `${prefix}_subcategory`,
            );
            return;
        }
        if (action === 'ambience-audio') {
            onEncounterAmbienceAudioSelected(`${prefix}_id`);
        }
    });
}


// ===========================================================================================
// Window attachment  the public surface the original top-level functions exposed. Every name has
// an out-of-range caller (PHP onclick / turn-widget bundle onclick / my_campaign.php setInterval).
// The two log_*_second_warning helpers are window-attached as the faithful default (they were
// top-level globals; over-exposing is harmless, a missed bare ref would crash the tick path).
// ===========================================================================================
declare global {
    interface Window {
        runTurnTimerTick: typeof runTurnTimerTick;
        gmControlsNextTurn: typeof gmControlsNextTurn;
        gmControlsPreviousTurn: typeof gmControlsPreviousTurn;
        getDefaultCreatureTurnTimerSecondsSync: typeof getDefaultCreatureTurnTimerSecondsSync;
        reinitializeTurnTimerUi: typeof reinitializeTurnTimerUi;
        ftRenderTurnTimerMirrorBadge: typeof ftRenderTurnTimerMirrorBadge;
        ftSyncTurnTimerMirror: typeof ftSyncTurnTimerMirror;
        ftEnsureTurnTimerMirrorTicker: typeof ftEnsureTurnTimerMirrorTicker;
        ftApplyTurnTimerMirror: typeof ftApplyTurnTimerMirror;
        broadcastTurnTimer: typeof broadcastTurnTimer;
        awardExperienceMarks: typeof awardExperienceMarks;
        viewEncounterLinks: typeof viewEncounterLinks;
        viewPublicEncounters: typeof viewPublicEncounters;
        viewPrivateEncounters: typeof viewPrivateEncounters;
        viewAllEncounters: typeof viewAllEncounters;
        copyAdventureToMasterParentID: typeof copyAdventureToMasterParentID;
        copySelectedLocationsToMasterParentID: typeof copySelectedLocationsToMasterParentID;
        moveSelectedToAdventure: typeof moveSelectedToAdventure;
        deleteAllLocationsExceptCampsiteAndStronghold: typeof deleteAllLocationsExceptCampsiteAndStronghold;
        linkPlayerToCampaign: typeof linkPlayerToCampaign;
        toggleCampaignRunning: typeof toggleCampaignRunning;
        toggleTurnTimer: typeof toggleTurnTimer;
        resetTurnTimer: typeof resetTurnTimer;
        addTurnTimerSeconds: typeof addTurnTimerSeconds;
        log_15_second_warning: typeof log_15_second_warning;
        log_10_second_warning: typeof log_10_second_warning;
    }
}

Object.assign(window, {
    runTurnTimerTick,
    gmControlsNextTurn,
    gmControlsPreviousTurn,
    getDefaultCreatureTurnTimerSecondsSync,
    reinitializeTurnTimerUi,
    ftRenderTurnTimerMirrorBadge,
    ftSyncTurnTimerMirror,
    ftEnsureTurnTimerMirrorTicker,
    ftApplyTurnTimerMirror,
    broadcastTurnTimer,
    awardExperienceMarks,
    viewEncounterLinks,
    viewPublicEncounters,
    viewPrivateEncounters,
    viewAllEncounters,
    copyAdventureToMasterParentID,
    copySelectedLocationsToMasterParentID,
    moveSelectedToAdventure,
    deleteAllLocationsExceptCampsiteAndStronghold,
    linkPlayerToCampaign,
    toggleCampaignRunning,
    toggleTurnTimer,
    resetTurnTimer,
    addTurnTimerSeconds,
    log_15_second_warning,
    log_10_second_warning,
});

wireCampaignToolActions();
wireModifyEncounterPageActions();

export {};
